import { Expose } from 'class-transformer';
import { PccAuditInfo } from './audit-info.typings';

export class PccAssessmentConversionWarning {
	section = '';

	message = '';

	questionKey?: string;

	constructor(partial?: Partial<PccAssessmentConversionWarning>) {
		if (partial) Object.assign(this, partial);
	}
}

export class PccAssessmentConversionResult {
	data: any = null;

	pccHippsCode = '';

	warnings: PccAssessmentConversionWarning[] = [];

	unmappedFields: string[] = [];

	constructor(partial?: Partial<PccAssessmentConversionResult>) {
		if (partial) Object.assign(this, partial);
		this.warnings = this.warnings?.map((w) => new PccAssessmentConversionWarning(w)) ?? [];
	}
}

export type PccAssessmentStatus = 'Complete' | 'Errors' | 'In Progress' | 'Inactivated' | 'Incomplete' | 'Modified';
/**
 * - txt: Text box question
 * - dte: Question with Date Controls
 * - dttm: Question with Date and Time Controls
 * - rad: Multiple Choice Question with Vertical Radio Buttons.
 * - radh: Multiple Choice Question with Horizontal Radio Buttons.
 * - chk: Yes/No Question with a Checkbox.
 * - mcs: Multiple Select Question with Vertical Checkboxes.
 * - mcsh: Multiple Select Question with Horizontal Checkboxes.
 * - num: Question with positive integer values only.
 * - numde: Question with positive integer values with decimals only..
 * - hck: Multiple Choice Question with Numbered response. Used in conjunction with a legend that would have a numeric value and associated description of the choices.
 * - cmb: Multiple Choice Question with user defined list box.
 * - pop: Question with search functions.
*/
export type PccAssessmentQuestionType = 'rad' | 'radh' | 'mcs' | 'mcsh' | 'hck' | 'cmb' | 'pop' | 'txt' | 'dte' | 'dttm' | 'num' | 'numde' | 'chk';

export class PccAssessmentInfoParams {
	/**
	 * When set to true this will take precedence over startDate and endDate and only the most recently created assessment for each unique template id will be returned. This parameter will default to false if not provided.
	 */
	latestTemplateVersion?: boolean;

	/**
	 * The end of the date range for which assessments will be returned for a patient. The date and time should be expressed in UTC in the following format: YYYY-MM-DDTHH:MM:SS.sssZ. If endDate is not specified it will default to the current date and time. If both startDate and endDate are not specified then the last 30 days of assessments will be returned. If the latestTemplateVersion parameter is set to true it will take precedence over startDate and endDate and only the most recently created assessment for each unique templateId will be returned.
	 */
	endDate?: string;

	/**
	 * The start of the date range for which assessments will be returned for a patient. The date and time should be expressed in UTC in the following format: YYYY-MM-DDTHH:MM:SS.sssZ. If startDate is not specified it will default to January 1, 1970 UTC. If both startDate and endDate are not specified then the last 30 days of assessments will be returned. If the latestTemplateVersion parameter is set to true it will take precedence over startDate and endDate and only the most recently created assessment for each unique templateId will be returned.
	 */
	startDate?: string;

	/**
	 * Unique template identifier for system-defined assessment
	 */
	systemTemplateId?: number;

	/**
	 * Unique template identifier associated with custom assessment.
	 */
	templateId?: number;

	/**
	 * Identifies the template version of all assessments, this includes both system-defined and custom assessments.
	 */
	templateVersion?: number;

	/**
	 * The status of the assessment. Acceptable values are "All, Complete, Errors, In Progress, Inactivated, Incomplete, Modified". Multiple comma separated values are accepted. If no value is passed only completed records are returned.
	 */
	assessmentStatuses?: Array<PccAssessmentStatus | 'All'>;
}

/**
 * Pcc Assessment Detail Response Value
 *
 * SimpliScreen maps the assessment detail response value to the following string for embedding in the RAG:
 * responseText: {responseText}, responseValue: {responseValue}
 */
export class PccAssessmentDetailResponseValue {
	/**
	 * The text displayed for the response entered for that question, if different from responseValue.

	 * Question types with responseText

	 * rad
	 * radh
	 * mcs
	 * mcsh
	 * hck
	 * cmb
	 * pop
	 */
	responseText: string;

	/**
	 * The value of the response entered for that question.
	 *
	 * Response value legend:
	 *
	 * - txt: The value entered in the field.
	 * - dte: The date format is YYYY-MM-DD.
	 * - dttm: The date and time are expressed in UTC in the following format: YYYY-MM-DDTHH:MM:SS.sssZ.
	 * - rad: The value associated to the field.
	 * - radh: The value associated to the field.
	 * - chk: The value associated to the field. 0 - Option not selected. 1 - Option Selected.
	 * - mcs: The value associated to the field.
	 * - mcsh: The value associated to the field.
	 * - num: The value associated to the field. Returns positive integers only.
	 * - numde: The value associated to the field. Returns positive integers with decimals only.
	 * - hck: The user defined value associated to the field.
	 * - cmb: The user defined value associated to the field.
	 * - pop: The user defined value associated to the field. Returns two user defined input boxes. User defined code:1,2.3, ar,en,dc. User defined text definition.
	 */
	responseValue: string;

	constructor(partial: Partial<PccAssessmentDetailResponseValue>) {
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails(prefix = '') {
		const details = [];
		if (this.responseText) details.push(`Text: ${this.responseText}`);
		if (this.responseValue) details.push(`Value: ${this.responseValue}`);
		return `${prefix}${details.join(', ')}`;
	}
}

/**
 * Pcc Assessment Detail Response
 * SimpliScreen maps the assessment detail response to the following string for embedding in the RAG:
 *
 * questionKey: {questionKey}, questionNumber: {questionNumber}, questionText: {questionText}, responses: {responses}
 * \t responses: {responseText}, responseValue: {responseValue}
 */
export class PccAssessmentQuestion {
	/**
	 * The identifier associated with the specified question within the question group of the assessment.
	 */
	questionKey: string;

	/**
	 * The letter or number associated with a specific question.
	 */
	questionNumber: string;

	/**
	 * The text description of the question.
	 */
	questionText: string;

	/**
	 * The maximum length of a response for this question.
	 */
	length: number;

	/**
	 * The title associated with the question.
	 */
	questionTitle: string;

	/**
	 * The type of question.
	 */
	questionType: PccAssessmentQuestionType;

	/**
	 * The range associated to the assessment template.
	 */
	range: string;

	/**
	 * A flag designating if a response is required for this question.
	 */
	required: boolean;

	/**
	 * List of possible responses for this question.
	 */
	responseOptions: PccAssessmentDetailResponseValue[];

	/**
	 * List of response(s) entered for that question.
	 */
	responses: PccAssessmentDetailResponseValue[];

	constructor(partial: Partial<PccAssessmentQuestion>) {
		Object.assign(this, partial);
		this.responses = this.responses?.map((response) => new PccAssessmentDetailResponseValue(response));
	}

	@Expose({ name: 'details' })
	getDetails(prefix = '') {
		const details = [];
		if (this.questionKey) details.push(`Question Key: ${this.questionKey}`);
		if (this.questionText) details.push(`Question Text: ${this.questionText}`);
		if (this.questionTitle) details.push(`Question Title: ${this.questionTitle}`);
		if (this.questionType) details.push(`Question Type: ${this.questionType}`);
		if (this.required) details.push(`Required: ${this.required}`);
		if (this.responseOptions) details.push(`\n${prefix}Response Options:\n${this.responseOptions.map((option) => option.getDetails(`${prefix}\t`)).join('\n')}`);
		if (this.responses) details.push(`\n${prefix}Responses:\n${this.responses.map((response) => response.getDetails(`${prefix}\t`)).join('\n')}`);
		return `${prefix}${details.join(', ')}`;
	}
}

/**
 * PccAssessmentDetailResponse
 *
 * SimpliScreen maps the assessment detail question group to the following string for embedding in the RAG:
 * groupNumber: {groupNumber}, groupTitle: {groupTitle}, assessmentResponses: {assessmentResponses}
 * \t assessmentResponses: {questionKey}, questionNumber: {questionNumber}, questionText: {questionText}, responses: {responses}
 * \t\t responses: {responseText}, responseValue: {responseValue}
 */
export class PccAssessmentDetailQuestionGroup {
	/**
	 * The designated letter or number that is associated with the question group.
	 */
	groupNumber: string;

	/**
	 * Optional text describing the question group, may be left blank by the user.
	 */
	groupText: string;

	/**
	 * The title associated with the question group of specified questions of the assessment.
	 */
	groupTitle: string;

	/**
	 * List of responses from the patient.
	 */
	assessmentResponses: PccAssessmentQuestion[];

	/**
	 * List of questions either selected through the standard question library or custom question entered by the user.
	 */
	questions: PccAssessmentQuestion[];

	constructor(partial: Partial<PccAssessmentDetailQuestionGroup>) {
		Object.assign(this, partial);
		this.assessmentResponses = this.assessmentResponses?.map((response) => new PccAssessmentQuestion(response));
	}

	@Expose({ name: 'details' })
	getDetails(prefix = '') {
		const details = [];
		if (this.groupNumber) details.push(`Group Number: ${this.groupNumber}`);
		if (this.groupText) details.push(`Group Text: ${this.groupText}`);
		if (this.groupTitle) details.push(`Group Title: ${this.groupTitle}`);
		if (this.assessmentResponses) details.push(`\n${prefix}Assessment Responses:\n${this.assessmentResponses.map((response) => response.getDetails(`${prefix}\t`)).join('\n')}`);
		return `${prefix}${details.join(', ')}`;
	}
}

export class PccAssessmentSection {
	/**
	 * The recorded client/user name associated with the completion of documentation of the assessment.
	 */
	completedBy: string;

	/**
	 * Represents the date the documentation was completed for the specified section of the assessment. The date and time are expressed in UTC in the following format YYYY-MM-DDTHH:MM:SS.sssZ.
	 */
	completedDate: string;

	/**
	 * The identification number associated with the selected section of the assessment.
	 */
	sectionCode: string;

	/**
	 * The title of the selected section of the assessment.  Note: sectionDescription is an optional field and can be blank.
	 */
	sectionDescription: string;

	/**
	 * The status of the specified section of the assessment.
	 */
	sectionStatus: 'Errors' | 'In Progress' | 'Not Applicable' | 'Signed' | 'Unedited';

	/**
	 * List of designated groupings associated with specified questions of the assessment .
	 */
	assessmentQuestionGroups: PccAssessmentDetailQuestionGroup[];

	constructor(partial: Partial<PccAssessmentSection>) {
		Object.assign(this, partial);
		this.assessmentQuestionGroups = this.assessmentQuestionGroups?.map((group) => new PccAssessmentDetailQuestionGroup(group));
	}

	@Expose({ name: 'details' })
	getDetails(prefix = '') {
		const details = [];
		if (this.completedBy) details.push(`Completed By: ${this.completedBy}`);
		if (this.completedDate) details.push(`Completed Date: ${this.completedDate}`);
		if (this.sectionDescription) details.push(`Section Description: ${this.sectionDescription}`);
		if (this.sectionCode) details.push(`Section Code: ${this.sectionCode}`);
		if (this.sectionStatus) details.push(`Section Status: ${this.sectionStatus}`);
		if (this.assessmentQuestionGroups) details.push(`\n${prefix}Assessment Question Groups:\n${this.assessmentQuestionGroups.map((group) => group.getDetails(`${prefix}\t`)).join('\n')}`);
		return `${prefix}${details.join(', ')}`;
	}
}

/**
 * PCC Assessment Information
 *
 * Assessments include detailed information on the assessment type, description, status, score, and template.
 *
 * SimpliScreen maps the assessment to the following string for embedding in the RAG:
 * Assessment: {assessmentDescription}, Status: {assessmentStatus}, Score: {assessmentScore}, Template: {templateDescription}
 * \t sections: {completedBy}, {completedDate}, {sectionCode}, {sectionStatus}
 * \t\t assessmentResponses: {questionKey}, questionNumber: {questionNumber}, questionText: {questionText}, responses: {responses}
 * \t\t\t responses: {responseText}, responseValue: {responseValue}
 *
 * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/assessments?patientId={patientId}
 */
export class PccAssessmentInfo extends PccAuditInfo {
	/**
	 * PointClickCare's unique identifier for the organization.
	 */
	orgId: number;

	/**
	 * PointClickCare's unique identifier for the organization.
	 */
	orgUuid: string;

	/**
	 * PointClickCare's unique identifier for a facility within the organization.
	 */
	facId: number;

	/**
	 * The identification number associated with the assessment.
	 */
	assessmentId: number;

	/**
	 * The date the assessment is created with the associated assessment identification number. The date and time are expressed in UTC in the following format YYYY-MM-DDTHH:MM:SS.sssZ.
	 */
	assessmentRefDate: string;

	/**
	 * The title of the assessment defined by the end user.
	 */
	assessmentDescription: string;

	/**
	 * The overall assessment status to determine documentation progress or completion.
	 *
	 * Possible values are:
	 * - Complete
	 * - Errors
	 * - In Progress
	 * - Inactivated
	 * - Incomplete
	 * - Modified
	 */
	assessmentStatus: PccAssessmentStatus;

	/**
	 * The calculated measurement of all question key responses within the assessment to determine the care level progress or decline on a client.
	 *
	 * Note: This field may be blank if not applicable for the specified assessment.
	 */
	assessmentScore: number;

	/**
	 * Unique template identifier associated with custom assessment.
	 */
	templateId: number;

	/**
	 * Identifies the template version of all assessments, this includes both system-defined and custom assessments.
	 */
	templateVersion: number;

	/**
	 * The possible types associated to the assessment, by the user, to determine assessment completion frequency.

	 * - Admissions
	 * - Comprehensive
	 * - Discharge
	 * - Full Assessment
	 * - Inactivation
	 * - Other
	 * - Quarterly
	 * - Reentry
	 */
	assessmentTypeDescription: 'Admissions' | 'Comprehensive' | 'Discharge' | 'Full Assessment' | 'Inactivation' | 'Other' | 'Quarterly' | 'Reentry';

	/**
	 * Unique template identifier for system-defined assessment
	 */
	systemTemplateId: number;

	/**
	 * The MDS assessment category: PPS (Medicare) or OBRA (federal requirement).
	 * Populated by Simpli360 when listing assessments.
	 */
	mdsCategory?: 'pps' | 'obra';

	/**
	 * The raw MDS type code (A0310B for PPS, A0310A for OBRA).
	 * Populated by Simpli360 when listing assessments.
	 */
	mdsTypeId?: string;

	/**
	 * The HIPPS code from the assessment (Z0100A).
	 * Populated by Simpli360 when listing PPS assessments.
	 */
	hippsCode?: string;

	/**
	 * Human-readable label for the MDS assessment type.
	 */
	@Expose()
	get mdsTypeLabel(): string {
		if (!this.mdsTypeId || !this.mdsCategory) return '';

		const labels: Record<string, Record<string, string>> = {
			pps: { '01': '5-day', '02': '14-day', '03': '30-day', '04': '60-day', '05': '90-day', '06': 'Readmission/return', '07': 'Unscheduled', '08': 'IPA' },
			obra: { '01': 'Admission', '02': 'Quarterly', '03': 'Annual', '04': 'Significant Change', '05': 'Correction (Comprehensive)', '06': 'Correction (Quarterly)' }
		};
		return labels[this.mdsCategory]?.[this.mdsTypeId] || this.mdsTypeId;
	}

	constructor(partial: Partial<PccAssessmentInfo>) {
		super();
		Object.assign(this, partial);
	}
}
/**
 * Pcc Full Assessment
 *
 * SimpliScreen maps the full assessment to the following string for embedding in the RAG:
 * assessmentDescription: {assessmentDescription}, assessmentStatus: {assessmentStatus}, assessmentScore: {assessmentScore}, templateDescription: {templateDescription}
 * \t sections: {completedBy}, {completedDate}, {sectionCode}, {sectionStatus}
 * \t\t assessmentResponses: {questionKey}, questionNumber: {questionNumber}, questionText: {questionText}, responses: {responses}
 * \t\t\t responses: {responseText}, responseValue: {responseValue}
 *
 * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/assessments/{assessmentId}
 * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/assessments?patientId={patientId}
 */
export class PccFullAssessment extends PccAssessmentInfo {
	/**
	 * The recorded locked date of the assessment. The assessment is no longer editable by the user. The date and time are expressed in UTC in the following format YYYY-MM-DDTHH:MM:SS.sssZ.
	 */
	lockedDate: string;

	/**
	 * PointClickCare's unique identifier for a patient within the organization.

	 * When a patient is admitted to a facility within an organization, they are assigned a patientId which is unique to their stay within that facility. If the same patient were to get admitted to another facility within the same organization in the future, they would be assigned a new patientId. The patientId however is unique at the organization level. That is, no two patients across different facilities within the organization will have the same patientId.
	 *
	 * **This identifier is crucial for tracking patient information and care but must not be displayed in any user interface due to legal privacy requirements.**
	 */
	patientId: number;

	/**
	 * 	The recorded date the assessment was struck out. The date and time are expressed in UTC in the following format YYYY-MM-DDTHH:MM:SS.sssZ.
	 */
	struckOutDate: string;

	/**
	 * Represents the reason why an assessment was struck out. Assessments may be struck out when they are incorrectly documented.
	 */
	struckOutReason: string;

	/**
	 * List of divided areas of the assessment set by the user.
	 */
	sections?: PccAssessmentSection[];

	constructor(partial: Partial<PccFullAssessment>) {
		super(partial);
		Object.assign(this, partial);
		this.sections = this.sections?.map((section) => new PccAssessmentSection(section));
	}

	@Expose({ name: 'details' })
	getDetails(prefix = '', skipDescription = false) {
		const details = [`Data Type: Assessment`];
		if (this.assessmentId) details.push(`Assessment ID: ${this.assessmentId}`);
		if (this.assessmentDescription && !skipDescription) details.push(`${prefix}Assessment Description: ${this.assessmentDescription}`);
		if (this.assessmentStatus) details.push(`${prefix}Assessment Status: ${this.assessmentStatus}`);
		if (this.assessmentScore) details.push(`${prefix}Assessment Score: ${this.assessmentScore}`);
		if (this.assessmentTypeDescription) details.push(`${prefix}Assessment Type Description: ${this.assessmentTypeDescription}`);
		if (this.struckOutDate) details.push(`${prefix}Struck Out Date: ${this.struckOutDate}`);
		if (this.struckOutReason) details.push(`${prefix}Struck Out Reason: ${this.struckOutReason}`);
		if (this.lockedDate) details.push(`${prefix}Locked Date: ${this.lockedDate}`);
		if (this.sections) details.push(`\n${prefix}Sections:\n${this.sections.map((section) => section.getDetails(`${prefix}\t`)).join('\n')}`);
		return `${prefix}${details.join(', ')}`;
	}

	static combineTemplateWithValues(assessment: Partial<PccFullAssessment>, template: Partial<PccFullAssessment>): PccFullAssessment {
		const fullAssessment = new PccFullAssessment(assessment);

		if (template.assessmentDescription && !fullAssessment.assessmentDescription) {
			fullAssessment.assessmentDescription = template.assessmentDescription;
		}

		if (!fullAssessment.sections || !template.sections) {
			return fullAssessment;
		}

		fullAssessment.sections.forEach((assessmentSection) => {
			const templateSection = template.sections.find((s: any) => s.sectionCode === assessmentSection.sectionCode);
			if (!templateSection) {
				return;
			}

			if (!assessmentSection.sectionDescription) {
				assessmentSection.sectionDescription = templateSection.sectionDescription;
			}

			if (!assessmentSection.assessmentQuestionGroups) {
				return;
			}

			assessmentSection.assessmentQuestionGroups.forEach((assessmentGroup) => {
				const templateGroup = templateSection.assessmentQuestionGroups.find((g: any) => g.groupNumber === assessmentGroup.groupNumber);
				if (!templateGroup) {
					return;
				}

				if (!assessmentGroup.groupText) {
					assessmentGroup.groupText = templateGroup.groupText;
				}

				if (!assessmentGroup.assessmentResponses) {
					return;
				}

				assessmentGroup.assessmentResponses.forEach((assessmentResponse) => {
					const templateQuestion = templateGroup.questions.find((q: any) => q.questionKey === assessmentResponse.questionKey);
					if (!templateQuestion) {
						return;
					}

					assessmentResponse.questionTitle = templateQuestion.questionTitle;
					assessmentResponse.questionType = templateQuestion.questionType;
					assessmentResponse.range = templateQuestion.range;
					assessmentResponse.required = templateQuestion.required;
					assessmentResponse.length = templateQuestion.length;

					if (templateQuestion.responseOptions) {
						assessmentResponse.responseOptions = templateQuestion.responseOptions.map((opt: any) => new PccAssessmentDetailResponseValue(opt));
					}
				});
			});
		});

		return fullAssessment;
	}
}
